import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { DateTime } from 'luxon'
import { Column } from 'primereact/column'
import { Fieldset } from 'primereact/fieldset'

import {
  hideLoadingBox,
  showLoadingBox,
  showMessageBox,
} from '../../../../store/actions/system.actions'
import {
  Btn,
  CustomDataTableHeader,
  CustomDropdown,
  CustomMultiSelect,
  CustomPanel,
  CustomDataTable,
  NumeroDecimal,
  PageHeader,
} from '../../../../components'
import { tableCellCentered } from '../../../../assets/js/styles'
import useSaldoMovimentacoesSaldoEstoque, {
  LOAD_MESSAGE,
} from './SaldoMovimentacoesSaldoEstoqueHook'
import ModalCriarMovimentacao from './ModalCriarMovimentacao'
import ModalConfirmarCriacaoMovimentacao from './ModalConfirmarCriacaoMovimentacao'

function parseDateValue(value) {
  if (!value) return null
  if (value instanceof Date) return DateTime.fromJSDate(value)

  if (typeof value === 'string') {
    if (value.includes('T')) return DateTime.fromISO(value)
    if (value.includes(' ')) return DateTime.fromFormat(value, 'yyyy-MM-dd HH:mm:ss')
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return DateTime.fromFormat(value, 'yyyy-MM-dd')
  }

  return null
}

function formatDate(value) {
  const parsedDate = parseDateValue(value)
  if (parsedDate && parsedDate.isValid) return parsedDate.toFormat('dd/MM/yyyy')
  return value || ''
}

function formatDateTime(value) {
  const parsedDate = parseDateValue(value)
  if (parsedDate && parsedDate.isValid) return parsedDate.toFormat('dd/MM/yyyy HH:mm:ss')
  return value || ''
}

function formatQuantity(value) {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0)
}

function buildConfirmationMessageData(result, form) {
  if (!result || !form.selectedEstoque || !form.selectedPosicaoEstoque || !form.selectedOperacaoEstoque) {
    return null
  }

  const isEntrada = form.selectedOperacaoEstoque.type === 'Entrada'
  return {
    tipoOperacao: isEntrada ? 'Entrada' : 'Saída',
    quantidade: `${isEntrada ? '+' : '-'}${formatQuantity(Math.abs(Number(form.quantidade) || 0))}`,
    produto: `${result.product.product_code} - ${result.product.name}`,
    posicao: form.selectedPosicaoEstoque.endereco,
    estoque: form.selectedEstoque.name,
    textoImpacto: isEntrada
      ? 'o produto não será adicionado ao estoque'
      : 'o produto não será removido do estoque, caso exista',
  }
}

function ComparisonBox({ label, value }) {
  return (
    <div className="p-col-12 p-md-4">
      <div
        style={{
          border: '1px solid #E5E5E5',
          borderRadius: 4,
          padding: 16,
          minHeight: 80,
        }}
      >
        <div style={{ color: '#14B0BF', fontSize: '0.85rem', marginBottom: 8 }}>{label}</div>
        <div style={{ fontSize: '1.15rem', fontWeight: 600 }}>
          <NumeroDecimal>{value}</NumeroDecimal>
        </div>
      </div>
    </div>
  )
}

function SaldoMovimentacoesSaldoEstoque({ dispatch, history, selectedTp }) {
  const { state, localDispatch } = useSaldoMovimentacoesSaldoEstoque()

  useEffect(() => {
    localDispatch.onInit(selectedTp)
  }, [selectedTp])

  useEffect(() => {
    if (state.loadingMessage === LOAD_MESSAGE.NONE) {
      dispatch(hideLoadingBox())
      return
    }

    dispatch(showLoadingBox('Aguarde...', state.loadingMessage))
  }, [state.loadingMessage])

  useEffect(() => {
    if (state.errorMessage) {
      dispatch(showMessageBox(null, state.errorMessage))
      localDispatch.resetState()
    }
  }, [state.errorMessage])

  useEffect(() => {
    if (state.successMessage) {
      dispatch(showMessageBox(state.successMessage, null))
      localDispatch.clearSuccessMessage()
    }
  }, [state.successMessage])

  const isTpInvalid = !selectedTp || selectedTp === 0

  return (
    <div>
      <PageHeader>S.31 - Saldo Movimentações x Saldo Estoque</PageHeader>
      <div style={{ marginTop: 20 }}>
        <CustomPanel onNovo={() => {}} showBtnNovo={false} onVoltar={history.goBack}>
          <div className="p-grid">
            <div className="p-col-12 p-md-3">
              <CustomMultiSelect
                disabled={isTpInvalid}
                label="Estoques"
                optionLabel="name"
                options={state.estoques}
                onChange={value => localDispatch.onChange('selectedEstoques', value)}
                value={state.selectedEstoques}
              />
            </div>
            <div className="p-col-12 p-md-3">
              <CustomDropdown
                label="Empresa"
                filter={true}
                optionLabel="name"
                options={state.empresas}
                value={state.selectedEmpresa}
                onChange={localDispatch.onEmpresaChange}
                disabled={isTpInvalid}
                placeholder="Selecione uma Empresa"
              />
            </div>
            <div className="p-col-12 p-md-4">
              <CustomDropdown
                label="Produto"
                filter={true}
                optionLabel="name"
                options={state.produtos}
                value={state.selectedProduto}
                onChange={value => localDispatch.onChange('selectedProduto', value)}
                disabled={isTpInvalid || !state.selectedEmpresa}
                placeholder="Selecione um Produto"
              />
            </div>
            <div
              className="p-col-12 p-md-2"
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: 16,
                flexWrap: 'wrap',
                paddingBottom: 8,
              }}
            >
              <Btn disabled={isTpInvalid} onClick={() => localDispatch.onPesquisar(selectedTp)}>
                Pesquisar
              </Btn>
              <Btn
                disabled={isTpInvalid || !state.result}
                onClick={() => localDispatch.onOpenCreateMovimentacao(selectedTp)}
              >
                Criar Movimentação
              </Btn>
            </div>
          </div>

          {state.result ? (
            <div className="p-grid">
              <div className="p-col-12" style={{ marginTop: 10 }}>
                <Fieldset legend="Estoque" toggleable collapsed={false}>
                  <CustomDataTable
                    value={state.result.estoque}
                    header={
                      <CustomDataTableHeader
                        onChange={value => localDispatch.onChange('globalFilterEstoque', value)}
                      />
                    }
                    globalFilter={state.globalFilterEstoque}
                    paginator
                    rows={10}
                    emptyMessage="Nenhum saldo de estoque encontrado para o produto selecionado"
                  >
                    <Column field="estoque_name" header="Estoque" sortable />
                    <Column field="posicao_endereco" header="Posição de Estoque" sortable />
                    <Column
                      field="lote"
                      header="Lote"
                      sortable
                      style={tableCellCentered(150)}
                      body={row => row.lote || ''}
                    />
                    <Column
                      field="validade"
                      header="Validade"
                      sortable
                      style={tableCellCentered(140)}
                      body={row => formatDate(row.validade)}
                    />
                    <Column
                      field="quantidade"
                      header="Quantidade"
                      sortable
                      style={tableCellCentered(140)}
                      body={row => <NumeroDecimal>{row.quantidade}</NumeroDecimal>}
                    />
                  </CustomDataTable>

                  <div style={{ marginTop: 16, textAlign: 'right', fontWeight: 600 }}>
                    Saldo Total:&nbsp;
                    <NumeroDecimal>{state.result.saldo_total_estoque}</NumeroDecimal>
                  </div>
                </Fieldset>
              </div>

              <div className="p-col-12" style={{ marginTop: 10 }}>
                <Fieldset legend="Movimentações" toggleable collapsed={false}>
                  <div className="p-grid" style={{ marginBottom: 8 }}>
                    <div className="p-col-12 p-md-4">
                      <strong>Período Inicial:</strong> {formatDateTime(state.result.periodo_inicial)}
                    </div>
                    <div className="p-col-12 p-md-4">
                      <strong>Período Final:</strong> {formatDateTime(state.result.periodo_final)}
                    </div>
                    <div className="p-col-12 p-md-4">
                      <strong>Saldo Inicial:</strong>{' '}
                      <NumeroDecimal>{state.result.saldo_inicial}</NumeroDecimal>
                    </div>
                  </div>

                  <CustomDataTable
                    value={state.result.movimentacoes}
                    header={
                      <CustomDataTableHeader
                        onChange={value =>
                          localDispatch.onChange('globalFilterMovimentacoes', value)
                        }
                      />
                    }
                    globalFilter={state.globalFilterMovimentacoes}
                    paginator
                    rows={10}
                    emptyMessage="Nenhuma movimentação encontrada no período considerado"
                  >
                    <Column
                      field="data_movimentacao"
                      header="Data"
                      sortable
                      style={tableCellCentered(180)}
                      body={row => formatDateTime(row.data_movimentacao)}
                    />
                    <Column field="estoque_name" header="Estoque" sortable />
                    <Column field="posicao_endereco" header="Posição de Estoque" sortable />
                    <Column field="operacao_estoque" header="Operação" sortable />
                    <Column
                      field="lote"
                      header="Lote"
                      sortable
                      style={tableCellCentered(130)}
                      body={row => row.lote || ''}
                    />
                    <Column
                      field="data_validade"
                      header="Validade"
                      sortable
                      style={tableCellCentered(140)}
                      body={row => formatDate(row.data_validade)}
                    />
                    <Column
                      field="entrada_saida"
                      header="Entrada/Saída"
                      sortable
                      style={tableCellCentered(140)}
                      body={row => <NumeroDecimal>{row.entrada_saida}</NumeroDecimal>}
                    />
                    <Column
                      field="saldo_final"
                      header="Saldo Final"
                      sortable
                      style={tableCellCentered(140)}
                      body={row => <NumeroDecimal>{row.saldo_final}</NumeroDecimal>}
                    />
                  </CustomDataTable>
                </Fieldset>
              </div>

              <div className="p-col-12" style={{ marginTop: 20 }}>
                <div style={{ color: '#14B0BF', fontSize: '0.9rem', marginBottom: 8 }}>
                  Comparação
                </div>
                <div className="p-grid">
                  <ComparisonBox
                    label="Saldo Total do Estoque"
                    value={state.result.saldo_total_estoque}
                  />
                  <ComparisonBox
                    label="Saldo Final das Movimentações"
                    value={state.result.saldo_final_movimentacoes}
                  />
                  <ComparisonBox label="Diferença" value={state.result.diferenca} />
                </div>
              </div>
            </div>
          ) : null}
        </CustomPanel>
      </div>

      <ModalCriarMovimentacao
        visible={state.showCreateMovimentacaoModal}
        onClose={localDispatch.onCloseCreateMovimentacao}
        result={state.result}
        estoques={state.estoques}
        posicoes={state.posicoesMovimentacao}
        operacoes={state.operacoesMovimentacao}
        form={state.createMovimentacaoForm}
        onEstoqueChange={value => localDispatch.onEstoqueMovimentacaoChange(selectedTp, value)}
        onChange={localDispatch.onChangeCreateMovimentacao}
        onSalvar={localDispatch.onOpenConfirmCriacao}
        loadingPosicoes={state.loadingPosicoesMovimentacao}
        loadingOperacoes={state.loadingOperacoesMovimentacao}
      />

      <ModalConfirmarCriacaoMovimentacao
        visible={state.showConfirmCriacaoModal}
        data={buildConfirmationMessageData(state.result, state.createMovimentacaoForm)}
        onConfirm={() => localDispatch.onConfirmCriacaoMovimentacao(selectedTp)}
        onCancel={localDispatch.onCloseConfirmCriacao}
        isLoading={state.savingMovimentacao}
      />
    </div>
  )
}

const mapStateToProps = ({ system: { selectedTp } }) => ({ selectedTp })
export default connect(mapStateToProps)(withRouter(SaldoMovimentacoesSaldoEstoque))
